/**
 * TransitionSystem — Directional scene transitions beyond plain fade.
 *
 * A cinematic upgrade over fadeOut → start → fadeIn. Each transition is
 * directional (where are you going?) and plays a corresponding whoosh SFX.
 *
 * Available transitions:
 *   - fade        : classic cross-fade (default, kept for compat)
 *   - irisClose   : circular mask shrinks to a point over center
 *   - irisOpen    : inverse — mask grows from center, used as intro
 *   - curtainDrop : black curtain slides down from top
 *   - parallaxPush: slides whole scene sideways (forward/backward)
 *   - radialWipe  : radial reveal from corner
 *
 * Usage:
 *   TransitionSystem.to(scene, 'MenuScene', { type: 'irisClose' });
 *   TransitionSystem.in(scene, 'irisOpen');  // call in new scene's create()
 */

import Phaser from 'phaser';
import { AudioManager } from './AudioManager';

export type TransitionType =
  | 'fade'
  | 'irisClose'
  | 'irisOpen'
  | 'curtainDrop'
  | 'parallaxPush'
  | 'radialWipe';

export interface TransitionOptions {
  type?: TransitionType;
  duration?: number;
  color?: number;
  direction?: 'left' | 'right' | 'up' | 'down';
  sfx?: boolean;
  data?: object;
}

export class TransitionSystem {
  /**
   * Transition OUT of current scene and start the target scene.
   */
  static to(
    scene: Phaser.Scene,
    target: string,
    opts: TransitionOptions = {},
  ): void {
    const {
      type = 'fade',
      duration = 500,
      color = 0x000000,
      direction = 'left',
      sfx = true,
      data,
    } = opts;

    // Lock to prevent double-transitions
    if ((scene as any)._transitioning) return;
    (scene as any)._transitioning = true;

    const onDone = () => {
      (scene as any)._transitioning = false;
      scene.scene.start(target, { ...data, _transitionIn: type });
    };

    if (sfx) this.playTransitionSFX(scene, type);

    switch (type) {
      case 'irisClose':
        this.irisClose(scene, duration, onDone);
        break;
      case 'curtainDrop':
        this.curtainDrop(scene, duration, color, onDone);
        break;
      case 'parallaxPush':
        this.parallaxPush(scene, duration, direction, onDone);
        break;
      case 'radialWipe':
        this.radialWipe(scene, duration, color, onDone);
        break;
      case 'fade':
      default:
        this.fade(scene, duration, color, onDone);
    }
  }

  /**
   * Transition INTO current scene (call from create() after setup).
   * Reads `_transitionIn` from scene data if present.
   */
  static in(scene: Phaser.Scene, type?: TransitionType, duration = 500): void {
    const data = scene.scene.settings.data as any;
    const t = type || (data?._transitionIn as TransitionType) || 'fade';

    switch (t) {
      case 'irisClose':
      case 'irisOpen':
        this.irisOpenIn(scene, duration);
        break;
      case 'curtainDrop':
        this.curtainRise(scene, duration);
        break;
      case 'parallaxPush':
        this.parallaxSlideIn(scene, duration);
        break;
      case 'radialWipe':
        this.radialReveal(scene, duration);
        break;
      case 'fade':
      default:
        scene.cameras.main.fadeIn(duration, 0, 0, 0);
    }
  }

  // ─── Individual transitions ────────────────────────────────────────────────

  private static fade(scene: Phaser.Scene, duration: number, color: number, onDone: () => void): void {
    const r = (color >> 16) & 0xFF;
    const g = (color >> 8) & 0xFF;
    const b = color & 0xFF;
    scene.cameras.main.fadeOut(duration, r, g, b);
    scene.cameras.main.once('camerafadeoutcomplete', onDone);
  }

  /**
   * Circle iris that shrinks from full-screen to zero over center.
   * Simulated with 4 black rectangles closing inward (Phaser doesn't
   * easily hole-punch Graphics in WebGL).
   */
  private static irisClose(scene: Phaser.Scene, duration: number, onDone: () => void): void {
    const { width, height } = scene.cameras.main;
    const top = scene.add.rectangle(0, 0, width, 0, 0x000000).setOrigin(0, 0);
    const bot = scene.add.rectangle(0, height, width, 0, 0x000000).setOrigin(0, 1);
    const lef = scene.add.rectangle(0, 0, 0, height, 0x000000).setOrigin(0, 0);
    const rig = scene.add.rectangle(width, 0, 0, height, 0x000000).setOrigin(1, 0);
    const allRects = [top, bot, lef, rig];
    allRects.forEach(r => r.setDepth(99999).setScrollFactor(0));

    scene.tweens.add({
      targets: [top, bot],
      height: height / 2,
      duration,
      ease: 'Cubic.easeIn',
    });
    scene.tweens.add({
      targets: [lef, rig],
      width: width / 2,
      duration,
      ease: 'Cubic.easeIn',
      onComplete: onDone,
    });
  }

  /**
   * Iris open — used as entrance. Draws 4 black rects that slide outward.
   */
  private static irisOpenIn(scene: Phaser.Scene, duration: number): void {
    const { width, height } = scene.cameras.main;
    const top = scene.add.rectangle(0, 0, width, height / 2, 0x000000).setOrigin(0, 0);
    const bot = scene.add.rectangle(0, height, width, height / 2, 0x000000).setOrigin(0, 1);
    const lef = scene.add.rectangle(0, 0, width / 2, height, 0x000000).setOrigin(0, 0);
    const rig = scene.add.rectangle(width, 0, width / 2, height, 0x000000).setOrigin(1, 0);
    const all = [top, bot, lef, rig];
    all.forEach(r => r.setDepth(99999).setScrollFactor(0));

    scene.tweens.add({
      targets: [top, bot],
      height: 0,
      duration,
      ease: 'Cubic.easeOut',
    });
    scene.tweens.add({
      targets: [lef, rig],
      width: 0,
      duration,
      ease: 'Cubic.easeOut',
      onComplete: () => all.forEach(r => r.destroy()),
    });
  }

  /**
   * Curtain drops from the top covering the whole screen.
   */
  private static curtainDrop(
    scene: Phaser.Scene,
    duration: number,
    color: number,
    onDone: () => void,
  ): void {
    const { width, height } = scene.cameras.main;
    const curtain = scene.add.rectangle(0, -height, width, height, color).setOrigin(0, 0);
    curtain.setDepth(99999).setScrollFactor(0);

    scene.tweens.add({
      targets: curtain,
      y: 0,
      duration,
      ease: 'Cubic.easeIn',
      onComplete: onDone,
    });
  }

  /**
   * Curtain rises off the screen (used as entrance).
   */
  private static curtainRise(scene: Phaser.Scene, duration: number): void {
    const { width, height } = scene.cameras.main;
    const curtain = scene.add.rectangle(0, 0, width, height, 0x000000).setOrigin(0, 0);
    curtain.setDepth(99999).setScrollFactor(0);

    scene.tweens.add({
      targets: curtain,
      y: -height,
      duration,
      ease: 'Cubic.easeOut',
      onComplete: () => curtain.destroy(),
    });
  }

  /**
   * Parallax push — slides a solid panel across the screen like a door.
   */
  private static parallaxPush(
    scene: Phaser.Scene,
    duration: number,
    direction: 'left' | 'right' | 'up' | 'down',
    onDone: () => void,
  ): void {
    const { width, height } = scene.cameras.main;
    let startX = 0, startY = 0, endX = 0, endY = 0;

    if (direction === 'left') { startX = width; endX = 0; }
    else if (direction === 'right') { startX = -width; endX = 0; }
    else if (direction === 'up') { startY = height; endY = 0; }
    else { startY = -height; endY = 0; }

    const panel = scene.add.rectangle(startX, startY, width, height, 0x000000).setOrigin(0, 0);
    panel.setDepth(99999).setScrollFactor(0);

    scene.tweens.add({
      targets: panel,
      x: endX,
      y: endY,
      duration,
      ease: 'Cubic.easeIn',
      onComplete: onDone,
    });
  }

  private static parallaxSlideIn(scene: Phaser.Scene, duration: number): void {
    const { width, height } = scene.cameras.main;
    const panel = scene.add.rectangle(0, 0, width, height, 0x000000).setOrigin(0, 0);
    panel.setDepth(99999).setScrollFactor(0);

    scene.tweens.add({
      targets: panel,
      x: -width,
      duration,
      ease: 'Cubic.easeOut',
      onComplete: () => panel.destroy(),
    });
  }

  /**
   * Radial wipe — circle grows from bottom-left corner.
   */
  private static radialWipe(
    scene: Phaser.Scene,
    duration: number,
    color: number,
    onDone: () => void,
  ): void {
    // Approximate with a circle that grows
    const { width, height } = scene.cameras.main;
    const circle = scene.add.circle(0, height, 10, color);
    circle.setDepth(99999).setScrollFactor(0);
    const targetR = Math.sqrt(width * width + height * height);

    scene.tweens.add({
      targets: circle,
      radius: targetR,
      duration,
      ease: 'Cubic.easeIn',
      onUpdate: () => {
        // Phaser circles don't auto-redraw on radius change — use scale
      },
    });
    scene.tweens.add({
      targets: circle,
      scale: targetR / 10,
      duration,
      ease: 'Cubic.easeIn',
      onComplete: onDone,
    });
  }

  private static radialReveal(scene: Phaser.Scene, duration: number): void {
    const { width, height } = scene.cameras.main;
    const circle = scene.add.circle(0, height, Math.sqrt(width * width + height * height), 0x000000);
    circle.setDepth(99999).setScrollFactor(0);
    circle.setScale(1);

    scene.tweens.add({
      targets: circle,
      scale: 0,
      duration,
      ease: 'Cubic.easeOut',
      onComplete: () => circle.destroy(),
    });
  }

  // ─── SFX dispatch ──────────────────────────────────────────────────────────

  private static playTransitionSFX(scene: Phaser.Scene, type: TransitionType): void {
    const audio = AudioManager.getInstance();
    const map: Record<TransitionType, string> = {
      fade:         'sfx_whoosh_transition',
      irisClose:    'sfx_iris_open',
      irisOpen:     'sfx_iris_open',
      curtainDrop:  'sfx_curtain_drop',
      parallaxPush: 'sfx_whoosh_transition',
      radialWipe:   'sfx_whoosh_reveal',
    };
    const key = map[type] || 'sfx_whoosh_transition';
    if (!scene.cache.audio.exists(key)) return;
    try {
      audio.playSFX(scene, key, { volume: 0.5 });
    } catch {}
  }
}
